import env, { isWooConfigured } from "@/config/env";
import { storeGet, storeGetList } from "@/api/wooClient";

// Data facade for the whole storefront. Order of precedence:
//   1. Real WooCommerce Store API  (when REACT_APP_WOO_STORE_API_URL is set)
//   2. Isolated mock catalog       (dev/preview only, REACT_APP_USE_MOCK=true)
//   3. Empty states                (nothing configured)
// Components never talk to WooCommerce directly — only through here — so the
// mock layer is fully removable without touching any UI code.

const mockMode = () => !isWooConfigured() && env.useMock;
const loadMock = () => import("@/mock/catalog");

function toMajor(value, minor = 2) {
  if (value === null || value === undefined || value === "") return null;
  return Number(value) / Math.pow(10, minor);
}

// Normalizes a WooCommerce Store API product into the stable internal shape
// used everywhere in the UI.
export function normalizeProduct(p) {
  const pr = p.prices || {};
  const minor = pr.currency_minor_unit ?? 2;
  const regular = toMajor(pr.regular_price, minor);
  const sale = toMajor(pr.sale_price, minor);
  return {
    id: p.id,
    slug: p.slug,
    name: p.name,
    image: p.images?.[0]?.src || null,
    images: (p.images || []).map((i) => ({ src: i.src, alt: i.alt || p.name })),
    price: sale ?? toMajor(pr.price, minor),
    regularPrice: regular,
    salePrice: sale,
    currency: pr.currency_code || env.defaultCurrency,
    onSale: Boolean(p.on_sale),
    featured: Boolean(p.featured),
    rating: Number(p.average_rating) || 0,
    reviewCount: p.review_count || 0,
    inStock: p.is_in_stock !== false,
    categories: (p.categories || []).map((c) => ({ id: c.id, name: c.name, slug: c.slug })),
    shortDescription: p.short_description || "",
    description: p.description || "",
    attributes: (p.attributes || []).map((a) => ({
      name: a.name,
      terms: (a.terms || []).map((t) => t.name),
    })),
    variations: p.variations || [],
  };
}

const WOO_ORDERBY = {
  relevance: {},
  price_asc: { orderby: "price", order: "asc" },
  price_desc: { orderby: "price", order: "desc" },
  rating: { orderby: "rating", order: "desc" },
  newest: { orderby: "date", order: "desc" },
};

export async function getCategories() {
  if (isWooConfigured()) {
    const data = await storeGet("/products/categories", { per_page: 100, hide_empty: true });
    return (data || []).map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
      count: c.count,
      image: c.image?.src || null,
    }));
  }
  if (mockMode()) return (await loadMock()).categories;
  return [];
}

export async function getProducts(opts = {}) {
  const {
    page = 1,
    perPage = 24,
    category,
    search,
    sort = "relevance",
    minPrice,
    maxPrice,
    onSale,
    featured,
  } = opts;

  if (isWooConfigured()) {
    const params = {
      page,
      per_page: perPage,
      category,
      search,
      min_price: minPrice,
      max_price: maxPrice,
      on_sale: onSale ? true : undefined,
      featured: featured ? true : undefined,
      ...(WOO_ORDERBY[sort] || {}),
    };
    const { items, total, totalPages } = await storeGetList("/products", params);
    return { items: items.map(normalizeProduct), total, totalPages };
  }
  if (mockMode()) return (await loadMock()).queryProducts(opts);
  return { items: [], total: 0, totalPages: 0 };
}

export async function getProduct(slug) {
  if (isWooConfigured()) {
    const data = await storeGet("/products", { slug });
    return data && data[0] ? normalizeProduct(data[0]) : null;
  }
  if (mockMode()) return (await loadMock()).getProductBySlug(slug);
  return null;
}

export async function searchSuggestions(term) {
  if (!term || term.length < 2) return [];
  if (isWooConfigured()) {
    const data = await storeGet("/products", { search: term, per_page: 6 });
    return (data || []).map((p) => ({ slug: p.slug, name: p.name }));
  }
  if (mockMode()) return (await loadMock()).suggest(term);
  return [];
}
